import React, { FC, useCallback, useEffect, useState } from 'react';
import { Alert, AlertIcon, Badge, Box, Flex, Heading, HStack, Img, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import SignInForm from '../components/SignInForm';
import VerifyCodeForm from '../components/VerifyCodeForm';
import NewPasswordForm from '../components/NewPasswordForm';
import ForgotPasswordForm from '../components/ForgotPasswordForm';
import ResetPasswordForm from '../components/ResetPasswordForm';
import { AuthStatus, useAuthDispatch, useAuthState } from '../contexts/AuthContext';
import { UnderlineLink } from '../components/chakraUIPro/UnderlineLink';
import Page from '../components/Page';
import Loading from '../components/Loading';

// eslint-disable-next-line no-shadow
enum FormStates {
  // LOGIN,
  NEW_PASSWORD,
  CONFIRM_EMAIL
  // FORGOT_PASSWORD,
  // RESET_PASSWORD,
  // CONFIRM_SIGNUP
}

const SignIn: FC = () => {
  const router = useRouter();
  const { isAuthenticated, sessionStatus, error } = useAuthState();
  const { dispatch } = useAuthDispatch();
  const [tmpUser, setTmpUser]: [any, any] = useState({});
  const [formState, setFormState] = useState<FormStates>();

  // Redirect to account page on sign-in
  useEffect(() => {
    if (isAuthenticated) {
      router.push('/account');
    }
  }, [isAuthenticated]);

  const clearSession = () => {
    dispatch({ type: 'clearSession' });
  };

  async function onLogin(nextStep: string, user: any) {
    setTmpUser(user);

    if (nextStep) {
      if (nextStep === 'NEW_PASSWORD_REQUIRED') {
        setFormState(FormStates.NEW_PASSWORD);
        return;
      }

      if (nextStep === 'UserNotConfirmedException') {
        setFormState(FormStates.CONFIRM_EMAIL);
      }
    }
  }

  const getAuthStep = useCallback(() => {
    let step: React.ReactNode;
    switch (sessionStatus) {
      case AuthStatus.ANONYMOUS:
        step = <SignInForm callback={onLogin} />;
        break;
      case AuthStatus.SIGN_UP_CONFIRM:
      case AuthStatus.PENDING_SIGN_UP_CONFIRM:
      case AuthStatus.SIGN_UP_CONFIRM_ERROR:
        step = <VerifyCodeForm type='CONFIRM_SIGN_UP' user={tmpUser} callback={clearSession} />;
        break;

      // Reset password
      case AuthStatus.RESET_PASSWORD:
      case AuthStatus.PENDING_RESET_PASSWORD:
      case AuthStatus.RESET_PASSWORD_ERROR:
        step = <ForgotPasswordForm callback={setTmpUser} />;
        break;

      // Reset password confirm
      case AuthStatus.RESET_PASSWORD_CONFIRM:
      case AuthStatus.PENDING_RESET_PASSWORD_CONFIRM:
      case AuthStatus.RESET_PASSWORD_CONFIRM_ERROR:
        step = <ResetPasswordForm email={tmpUser?.email} />;
        break;

      default:
        step = <Loading h='auto' />;
    }

    return step;
  }, [sessionStatus, tmpUser]);

  return (
    <Page
      seoOptions={{
        title: 'Vitamin Club Login - Family Health Simplified',
        description:
          "Sign in to Vitamin Club for personalized, dietitian-certified vitamins tailored for your family's health " +
          'needs. Simplify your wellness journey today.',
        canonical: 'https://vitaminclub.co.za/sign-in/'
      }}
      maxW='100%'
      p={0}
      m={0}
    >
      <>
        <Flex direction={{ base: 'column', md: 'row' }} overflow='hidden' minH='100vh' height='100%' bg='gray.50'>
          <Box overflowY='auto' flex='1' py={{ base: '10', md: '16' }} px={{ base: '6', md: '10' }}>
            <Box maxW='sm' mx='auto'>
              <Box textAlign='center' mb={{ base: '10', md: '16' }}>
                <Heading as='h1' size='xl' fontWeight='extrabold' letterSpacing='tight'>
                  Sign in to your account
                </Heading>
                <HStack spacing={2} mt={3} justify='center'>
                  <Text color='gray.600' fontWeight='medium'>
                    Need an account?
                  </Text>
                  <UnderlineLink as={NextLink} href='/sign-up/'>
                    Sign up for free
                  </UnderlineLink>
                </HStack>
              </Box>
              {/* Errors */}
              {error && (
                <Alert status='error' mb='4' borderRadius='md'>
                  <AlertIcon />
                  {error?.message}
                </Alert>
              )}
              {/* Auth step */}
              {getAuthStep()}
              {/* Additional steps */}
              {formState === FormStates.CONFIRM_EMAIL && <VerifyCodeForm user={tmpUser} callback={clearSession} />}
              {formState === FormStates.NEW_PASSWORD && <NewPasswordForm callback={clearSession} />}
            </Box>
          </Box>

          <Box display={{ base: 'none', lg: 'block' }} flex='1' bg='gray.500' color='white' px='20' pt='32'>
            <Badge bg='blackAlpha.200' px='4' py='1' rounded='md' letterSpacing='wide' color='whiteAlpha.900'>
              Vitamins should be simple!
            </Badge>
            <Text
              mt='6'
              fontWeight='extrabold'
              fontSize={{ base: '2xl', lg: '3xl' }}
              maxW='sm'
              letterSpacing='tight'
              lineHeight='normal'
            >
              Sign up for a free account today
            </Text>
            <Text mt='5' maxW='md' fontSize='lg'>
              Track your family's vitamin needs in one place. Sign-up is quick and checkout even quicker.
            </Text>
            <Box m='10' position='relative' insetEnd='6'>
              <Img alt='' src='/images/undraw-enter@2x.png' />
            </Box>
          </Box>
        </Flex>
      </>
    </Page>
  );
};
export default SignIn;
